import * as types from './ActionTypes';
import { gcApi } from '../config/axios';
import UrlParser from 'utils/parsers/UrlParser';

export const requestCourses = ({ displayName, poiLat, poiLon }, evictCache) => ({
    type: types.REQUEST_COURSES,
    displayName,
    poiLat,
    poiLon,
    meta: {
        cacheTime: evictCache ? null : 1000
    }
});

export const receiveCourses = (json) => ({
    type: types.RECEIVE_COURSES,
    courses: json,
    receivedAt: Date.now()
});

export const requestGroupCourses = (groupId) => ({
    type: types.REQUEST_GROUP_COURSES,
    groupId
});

export const receiveGroupCourses = (json) => ({
    type: types.RECEIVE_GROUP_COURSES,
    courses: json,
    receivedAt: Date.now()
});

export function fetchFavoriteCourses() {
    return {
        type: types.FETCH_FAVORITE_COURSES
    };
}

export function fetchCourseDetail(courseId, shouldFetchCourseSegments = false) {
    return {
        type: types.FETCH_COURSE_DETAIL,
        courseId: courseId,
        shouldFetchCourseSegments: shouldFetchCourseSegments
    };
}

export const requestWebGatewayCourses = (displayName, evictCache) => ({
    type: types.REQUEST_WEB_GATEWAY_COURSES,
    displayName: displayName,
    meta: {
        cacheTime: evictCache ? null : 60000
    }
});

export const requestWebGatewayCoursesForChina = (displayName, evictCache) => ({
    type: types.REQUEST_WEB_GATEWAY_COURSES_FOR_CHINA,
    displayName: displayName,
    meta: {
        cacheTime: evictCache ? null : 60000
    }
});

export const receiveWebGatewayCourses = (json) => ({
    type: types.RECEIVE_WEB_GATEWAY_COURSES,
    courses: json,
    receivedAt: Date.now()
});

export const receiveWebGatewayCoursesForChina = (json) => ({
    type: types.RECEIVE_WEB_GATEWAY_COURSES_FOR_CHINA,
    courses: json,
    receivedAt: Date.now()
});

export const addEditableCourseData = (
    editableCourseData,
    historyOverwriteIndex = false,
    doNotUpdateLBCP = false,
    keepGeoPointsDistance = false
) => ({
    type: types.ADD_EDITABLE_COURSE_DATA,
    editableCourseData: editableCourseData,
    historyOverwriteIndex: historyOverwriteIndex,
    doNotUpdateLBCP: doNotUpdateLBCP, // force Lab by Course Points to not re-calculate mile/km/half_marathon course points
    keepGeoPointsDistance // keep distance in geoPoints,
});

export const resetEditableCourseData = () => ({
    type: types.RESET_EDITABLE_COURSE_DATA
});

export const deleteAllCoursePointRests = () => ({ type: types.DELETE_COURSE_POINTS_RESTS });

export const deleteAllCoursePointCutoffs = () => ({ type: types.DELETE_COURSE_POINTS_CUTOFFS });

export const deleteAllCoursePointNotes = () => ({ type: types.DELETE_COURSE_POINTS_NOTES });

export const addImportedCourseData = (importedCourseData) => ({
    type: types.ADD_IMPORTED_COURSE_DATA,
    importedCourseData: importedCourseData
});

export const resetImportedCourseData = () => ({
    type: types.RESET_IMPORTED_COURSE_DATA
});

export const resetEditableCourseDataOnDrawingMethodChanged = () => ({
    type: types.RESET_EDITABLE_COURSE_DATA_ON_DRAWING_METHOD_CHANGED
});

export const addEditableCourseDrawingData = (editableCourseDrawingData) => ({
    type: types.ADD_EDITABLE_COURSE_DRAWING_DATA,
    editableCourseDrawingData: editableCourseDrawingData
});

export const createNewCourse = (payload) => ({
    type: types.CREATE_NEW_COURSE,
    payload: payload
});

export const setCourseRouteSegment = (payload) => ({
    type: types.SET_COURSE_ROUTE_SEGMENT,
    payload: payload
});

export const fetchCourseLineSegments = (courseId) => ({
    type: types.GET_COURSE_ROUTE_SEGMENT,
    courseId: courseId
});

export const updateCourseData = (payload, powerGuideIds, paceProIds, inlineMode = false) => ({
    type: types.UPDATE_COURSE_DATA,
    payload: payload,
    powerGuideIds,
    paceProIds,
    inlineMode
});

export const updateCoursePrivacy = (courseId, payload) => ({
    type: types.UPDATE_COURSE_PRIVACY,
    payload: payload,
    courseId: courseId
});

export const undoCourseRouteHistory = () => ({
    type: types.UNDO_COURSE_ROUTE_HISTORY
});

export const redoCourseRouteHistory = () => ({
    type: types.REDO_COURSE_ROUTE_HISTORY
});

export const setCourseElevationChartExpandedStatus = (status) => ({
    type: types.SET_COURSE_ELEVATION_CHART_EXPANDED_STATUS,
    status: status
});

export const resetCourseElevationChartExpandedStatus = () => ({
    type: types.RESET_COURSE_ELEVATION_CHART_EXPANDED_STATUS
});

export const setChartSelectedAreaStartEndValue = (selectionStart, selectionEnd) => ({
    type: types.SET_CHART_SELECTED_AREA_START_END_VALUE,
    selectionStart: selectionStart,
    selectionEnd: selectionEnd
});

export const resetChartSelectedAreaStartEndValue = () => ({
    type: types.RESET_CHART_SELECTED_AREA_START_END_VALUE
});

export const setElevationChartSelectedGeoPoints = (selectedGeoPoints) => ({
    type: types.SET_ELEVATION_CHART_SELECTED_GEO_POINTS,
    selectedGeoPoints: selectedGeoPoints
});

export const resetElevationChartSelectedGeoPoints = () => ({
    type: types.RESET_ELEVATION_CHART_SELECTED_GEO_POINTS
});

export const resetEditableCourseDrawingData = () => ({
    type: types.RESET_EDITABLE_COURSE_DRAWING_DATA
});

export function setCourseToFavorite(courseId) {
    return {
        type: types.SET_COURSE_TO_FAVORITE,
        courseId: courseId
    };
}

export function removeCourseFromFavorite(courseId) {
    return {
        type: types.REMOVE_COURSE_FROM_FAVORITE,
        courseId: courseId
    };
}

export function resetChangingFavoriteCourseStatusFailed() {
    return {
        type: types.RESET_CHANGING_FAVORITE_COURSE_STATUS_FAIL
    };
}

export function deleteCourse(courseId) {
    return {
        type: types.DELETE_COURSE_START,
        courseId: courseId
    };
}

export function resetDeleteCourseErrorStatus() {
    return {
        type: types.RESET_DELETE_COURSE_ERROR_STATUS
    };
}

export function fetchCourseRoundTripRoute(params) {
    return {
        type: types.FETCH_COURSE_ROUND_TRIP_ROUTE,
        params: params
        //params expected to be an object with startLat, startLon, direction, distance, courseType and if china extra param coordinateSystem
    };
}

export function fetchCoursePopularRoutes(params) {
    return {
        type: types.FETCH_COURSE_POPULAR_ROUTES,
        params: params
        //params expected to be an object with startLat, startLon, endLat,endLon, courseType and if china extra param sourceCoordinateSystem and targetCoordinateSystem
    };
}

/**
 *
 * @param {any[]} coursePointsArray
 * @param {Object} options
 * @param {() => any} [options.callback]
 * @param {boolean} [options.undo]
 * @returns
 */
export function fetchElevationForCoursePoints(coursePointsArray, options) {
    return {
        type: types.FETCH_ELEVATION_DATA_FOR_COURSE_POINTS,
        coursePointsArray: coursePointsArray, // array of points for witch to fetch the elevation
        callback: options?.callback,
        undo: options?.undo // action is triggered by undo (will skip unnecessary distance re-calculation)
    };
}

/**
 * Use once only on China import. this is for initial elevation fetching of WGS84 data points and elevation
 * @param {any[]} coursePointsArray
 * @returns
 */
export function fetchElevationForCoursePointsOnChinaImport(coursePointsArray) {
    return {
        type: types.FETCH_ELEVATION_DATA_FOR_COURSE_POINTS_ON_CHINA_IMPORT,
        coursePointsArray // array of points for witch to fetch the elevation,
    };
}

export function fetchAvailableCoursePoints() {
    return {
        type: types.FETCH_COURSE_POINTS
    };
}

export function onElevationChartHover(payload) {
    return {
        type: types.ELEVATION_CHART_HOVER,
        payload: payload
    };
}
export function resetElevationChartHover() {
    return {
        type: types.RESET_ELEVATION_CHART_HOVER
    };
}

export function setCoursePointSelection(pointType, pointLocation, editModeMarkerInitialValue) {
    let payload = {};

    if (pointType) {
        payload.pointType = pointType;
    }
    if (pointLocation) {
        payload.pointLocation = pointLocation;
    }
    if (editModeMarkerInitialValue) {
        payload.editModeMarkerInitialValue = editModeMarkerInitialValue;
    }

    return {
        type: types.SET_COURSE_POINT_SELECTION,
        payload: {
            ...payload
        }
    };
}

export function resetCoursePointSelection() {
    return {
        type: types.RESET_COURSE_POINT_SELECTION
    };
}

export const updateCourse = (payload) => {
    const { courseId, privacyRule } = payload;
    const url = `/course-service/course/${courseId}/privacy`;
    return gcApi.put(url, privacyRule);
};

export function fetchElevationStarted() {
    return {
        type: types.FETCH_ELEVATION_DATA_FOR_COURSE_POINTS_START
    };
}

export function fetchElevationEnded() {
    return {
        type: types.FETCH_ELEVATION_DATA_FOR_COURSE_POINTS_ENDED
    };
}

/**
 * this is used in case we do not want to store the points received - like when dragging a point from the middle of the course to redraw - the lines are created after
 * @param params
 * @returns {AxiosPromise}
 */
export const fetchPointPopularRoute = (params) => {
    const query = new UrlParser().convertObjectToQueryString(params);

    return gcApi.get(`/course-service/course/popularRoutes${query}`);
};

export const fetchCourseByCourseId = (courseId) => {
    return gcApi.get(`/course-service/course/${courseId}`);
};

export function setCoursePointMarkerData(payload) {
    return {
        type: types.SET_COURSE_MARKER_DATA,
        payload
    };
}

export function resetCoursePointMarkerData() {
    return {
        type: types.RESET_COURSE_MARKER_DATA
    };
}

export const requestNearbyCourses = (bounds, size = 10, fromCourseIndex = false, sort, filter = null) => ({
    type: types.REQUEST_NEARBY_COURSES,
    bounds: bounds,
    size: size,
    fromCourseIndex: fromCourseIndex,
    sort: sort,
    filter: filter
});

export const resetNearbyCourses = () => ({
    type: types.RESET_NEARBY_COURSES
});

export function fetchThirdPartyConsumerInfo(consumer, courseId, evictCache) {
    return {
        type: types.FETCH_THIRD_PARTY_CONSUMER_INFO,
        consumer: consumer,
        courseId: courseId,
        meta: {
            cacheTime: evictCache ? null : 60000
        }
    };
}

export const requestClimbs = (boundingBox, evictCache) => ({
    type: types.REQUEST_CLIMBS,
    boundingBox: boundingBox,
    meta: {
        cacheTime: evictCache ? null : 1000
    }
});

export const receiveClimbs = (json) => ({
    type: types.RECEIVE_CLIMBS,
    climbs: json,
    receivedAt: Date.now()
});

export const receiveClimbsOnCourseFailed = () => {
    return {
        type: types.RECEIVE_CLIMBS_FAILED
    };
};

export function resetCachedClimbs() {
    return {
        type: types.RESET_CACHED_CLIMBS
    };
}

export function fetchGeoPointsForCoursesMinimap(coursesList) {
    return {
        type: types.FETCH_GEOPOINTS_FOR_COURSES_MINIMAP,
        coursesList: coursesList
    };
}

export function resetGeoPointsForCoursesMinimap() {
    return {
        type: types.RESET_GEOPOINTS_FOR_COURSES_MINIMAP_LIST
    };
}

export function removeCachedMinimapRouteByIds(courseIds) {
    return {
        type: types.REMOVE_CACHED_MINIMAP_ROUTE_BY_IDS,
        courseIds: courseIds
    };
}

export function setCoursesListSortValue(courseListSortType, callback) {
    return {
        type: types.SET_COURSES_LIST_SORT_VALUE,
        courseListSortType: courseListSortType,
        callback: callback
    };
}
export function resetCoursesListSortValue() {
    return {
        type: types.RESET_COURSES_LIST_SORT_VALUE
    };
}

export function setCoursesListFilterValue(courseListFilters) {
    return {
        type: types.SET_COURSES_LIST_FILTER_VALUE,
        courseListFilters: courseListFilters
    };
}

export function resetCoursesListFilterValue() {
    return {
        type: types.RESET_COURSES_LIST_FILTER_VALUE
    };
}

export const requestClimbsOnRoute = (coursePoints, historyIndex, evictCache) => ({
    type: types.REQUEST_CLIMBS_ON_ROUTE,
    coursePoints: coursePoints,
    historyIndex: historyIndex,
    meta: {
        cacheTime: evictCache ? null : 1000
    }
});

export const receiveClimbsOnRoute = (json, historyIndex) => ({
    type: types.RECEIVE_CLIMBS_ON_ROUTE,
    climbsOnRouteOrCourse: json,
    historyIndex: historyIndex,
    receivedAt: Date.now()
});

export const requestClimbsOnCourse = (courseId, evictCache) => ({
    type: types.REQUEST_CLIMBS_ON_COURSE,
    courseId: courseId,
    meta: {
        cacheTime: evictCache ? null : 1000
    }
});

export const receiveClimbsOnCourse = (json) => ({
    type: types.RECEIVE_CLIMBS_ON_COURSE,
    climbsOnRouteOrCourse: json,
    receivedAt: Date.now()
});

export function duplicateCourse(courseId) {
    return {
        type: types.DUPLICATE_COURSE,
        courseId: courseId
    };
}

export const requestSecondaryClimbs = (climb, minDifficulty, maxDifficulty) => ({
    type: types.REQUEST_SECONDARY_CLIMBS,
    climb: climb,
    minDifficulty: minDifficulty,
    maxDifficulty: maxDifficulty,
    meta: {
        cacheTime: 1000
    }
});

export const receiveSecondaryClimbs = (json) => ({
    type: types.RECEIVE_SECONDARY_CLIMBS,
    secondaryClimbs: json,
    receivedAt: Date.now()
});

export function resetSecondaryClimbs() {
    return {
        type: types.RESET_SECONDARY_CLIMBS
    };
}

export function resetClimbsOnRouteOrCourse() {
    return {
        type: types.RESET_CLIMBS_ON_ROUTE_OR_COURSE
    };
}

export function fetchCoursePopularRoutesWithClimbs(params, climb, addClimbOnly) {
    return {
        type: types.FETCH_COURSE_POPULAR_ROUTES_AND_ADD_CLIMB,
        params: params,
        climb: climb,
        addClimbOnly: addClimbOnly
        //params expected to be an object with startLat, startLon, endLat,endLon, courseType and if china extra param sourceCoordinateSystem and targetCoordinateSystem
    };
}

export function requestCourseMapMatch(courseId) {
    return {
        type: types.REQUEST_COURSE_MAP_MATCH,
        courseId
    };
}

export function confirmElevationWasUpdatedByApi() {
    return {
        type: types.CONFIRM_ELEVATION_WAS_UPDATED_BY_API_FOR_COURSE
    };
}

export function resetCourseMapMatchStatus() {
    return {
        type: types.RESET_COURSE_MAP_MATCH_STATUS
    };
}

export function setMapBoundingBox(mapBoundingBox) {
    return {
        type: types.SET_MAP_BOUNDING_BOX,
        mapBoundingBox: mapBoundingBox
    };
}

export function resetMapBoundingBox() {
    return {
        type: types.RESET_MAP_BOUNDING_BOX
    };
}

export const requestHazards = (boundingBox, evictCache) => ({
    type: types.REQUEST_HAZARDS,
    boundingBox: boundingBox,
    meta: {
        cacheTime: evictCache ? null : 1000
    }
});

export const receiveHazards = (json) => ({
    type: types.RECEIVE_HAZARDS,
    hazards: json,
    receivedAt: Date.now()
});

export const receiveHazardsFailed = (json) => ({
    type: types.RECEIVE_HAZARDS,
    hazards: [],
    receivedAt: Date.now()
});

export const requestHazardsOnCourse = (courseId, evictCache) => ({
    type: types.REQUEST_HAZARDS_ON_COURSE,
    courseId: courseId,
    meta: {
        cacheTime: evictCache ? null : 1000
    }
});

export function resetHazards() {
    return {
        type: types.RESET_HAZARDS
    };
}

export function convertActivityToCourse(activityId, callback) {
    return {
        type: types.CONVERT_ACTIVITY_TO_COURSE,
        activityId,
        callback
    };
}

export function enableLapByCoursePoints(enabled) {
    return {
        type: types.ENABLE_LAP_BY_COURSE_POINTS,
        lapByCoursePointsEnabled: enabled
    };
}
